import { useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo, buttonVariants, cn } from '@examflow/ui';
import {
  buildExamSelectionPageModel,
  type ExamSelectionHeroProof,
  type ExamSelectionPageModel,
} from '@examflow/learning-space';
import { BeianFooter } from '../components/BeianFooter';
import { useLearningViewer } from '../features/learning-space/useLearningViewer';
import { buildPreferredWorkspaceHref } from '../lib/userPreferences';

/** Soft cool stage — BonTCF white + glow, ExamFlow sage (not cream terracotta). */
const STAGE_BG =
  'radial-gradient(ellipse 70% 55% at 85% 20%, rgba(91,122,91,0.14), transparent 55%), radial-gradient(ellipse 50% 45% at 10% 80%, rgba(200,90,26,0.07), transparent 50%), #f7f8f6';

const XIAOHONGSHU_PROFILE_URL =
  'https://www.xiaohongshu.com/user/profile/68af0e5a000000001900c81f';

/** Simple Icons Xiaohongshu brand mark (CC0). Brand red #FF2442. */
function XiaohongshuIcon({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M22.405 9.879c.002.016.01.02.07.019h.725a.797.797 0 0 0 .78-.972.794.794 0 0 0-.884-.618.795.795 0 0 0-.692.794c0 .101-.002.666.001.777zm-11.509 4.808c-.203.001-1.353.004-1.685.003a2.528 2.528 0 0 1-.766-.126.025.025 0 0 0-.03.014L7.7 16.127a.025.025 0 0 0 .01.032c.111.06.336.124.495.124.66.01 1.32.002 1.981 0 .01 0 .02-.006.023-.015l.712-1.545a.025.025 0 0 0-.024-.036zM.477 9.91c-.071 0-.076.002-.076.01a.834.834 0 0 0-.01.08c-.027.397-.038.495-.234 3.06-.012.24-.034.389-.135.607-.026.057-.033.042.003.112.046.092.681 1.523.787 1.74.008.015.011.02.017.02.008 0 .033-.026.047-.044.147-.187.268-.391.371-.606.306-.635.44-1.325.486-1.706.014-.11.021-.22.03-.33l.204-2.616.022-.293c.003-.029 0-.033-.03-.034zm7.203 3.757a1.427 1.427 0 0 1-.135-.607c-.004-.084-.031-.39-.235-3.06a.443.443 0 0 0-.01-.082c-.004-.011-.052-.008-.076-.008h-1.48c-.03.001-.034.005-.03.034l.021.293c.076.982.153 1.964.233 2.946.05.4.186 1.085.487 1.706.103.215.223.419.37.606.015.018.037.051.048.049.02-.003.742-1.642.804-1.765.036-.07.03-.055.003-.112zm3.861-.913h-.872a.126.126 0 0 1-.116-.178l1.178-2.625a.025.025 0 0 0-.023-.035l-1.318-.003a.148.148 0 0 1-.135-.21l.876-1.954a.025.025 0 0 0-.023-.035h-1.56c-.01 0-.02.006-.024.015l-.926 2.068c-.085.169-.314.634-.399.938a.534.534 0 0 0-.02.191.46.46 0 0 0 .23.378.981.981 0 0 0 .46.119h.59c.041 0-.688 1.482-.834 1.972a.53.53 0 0 0-.023.172.465.465 0 0 0 .23.398c.15.092.342.12.475.12l1.66-.001c.01 0 .02-.006.023-.015l.575-1.28a.025.025 0 0 0-.024-.035zm-6.93-4.937H3.1a.032.032 0 0 0-.034.033c0 1.048-.01 2.795-.01 6.829 0 .288-.269.262-.28.262h-.74c-.04.001-.044.004-.04.047.001.037.465 1.064.555 1.263.01.02.03.033.051.033.157.003.767.009.938-.014.153-.02.3-.06.438-.132.3-.156.49-.419.595-.765.052-.172.075-.353.075-.533.002-2.33 0-4.66-.007-6.991a.032.032 0 0 0-.032-.032zm11.784 6.896c0-.014-.01-.021-.024-.022h-1.465c-.048-.001-.049-.002-.05-.049v-4.66c0-.072-.005-.07.07-.07h.863c.08 0 .075.004.075-.074V8.393c0-.082.006-.076-.08-.076h-3.5c-.064 0-.075-.006-.075.073v1.445c0 .083-.006.077.08.077h.854c.075 0 .07-.004.07.07v4.624c0 .095.008.084-.085.084-.37 0-1.11-.002-1.304 0-.048.001-.06.03-.06.03l-.697 1.519s-.014.025-.008.036c.006.01.013.008.058.008 1.748.003 3.495.002 5.243.002.03-.001.034-.006.035-.033v-1.539zm4.177-3.43c0 .013-.007.023-.02.024-.346.006-.692.004-1.037.004-.014-.002-.022-.01-.022-.024-.005-.434-.007-.869-.01-1.303 0-.072-.006-.071.07-.07l.733-.003c.041 0 .081.002.12.015.093.025.16.107.165.204.006.431.002 1.153.001 1.153zm2.67.244a1.953 1.953 0 0 0-.883-.222h-.18c-.04-.001-.04-.003-.042-.04V10.21c0-.132-.007-.263-.025-.394a1.823 1.823 0 0 0-.153-.53 1.533 1.533 0 0 0-.677-.71 2.167 2.167 0 0 0-1-.258c-.153-.003-.567 0-.72 0-.07 0-.068.004-.068-.065V7.76c0-.031-.01-.041-.046-.039H17.93s-.016 0-.023.007c-.006.006-.008.012-.008.023v.546c-.008.036-.057.015-.082.022h-.95c-.022.002-.028.008-.03.032v1.481c0 .09-.004.082.082.082h.913c.082 0 .072.128.072.128V11.19s.003.117-.06.117h-1.482c-.068 0-.06.082-.06.082v1.445s-.01.068.064.068h1.457c.082 0 .076-.006.076.079v3.225c0 .088-.007.081.082.081h1.43c.09 0 .082.007.082-.08v-3.27c0-.029.006-.035.033-.035l2.323-.003c.098 0 .191.02.28.061a.46.46 0 0 1 .274.407c.008.395.003.79.003 1.185 0 .259-.107.367-.33.367h-1.218c-.023.002-.029.008-.028.033.184.437.374.871.57 1.303a.045.045 0 0 0 .04.026c.17.005.34.002.51.003.15-.002.517.004.666-.01a2.03 2.03 0 0 0 .408-.075c.59-.18.975-.698.976-1.313v-1.981c0-.128-.01-.254-.034-.38 0 .078-.029-.641-.724-.998z"
      />
    </svg>
  );
}

function scrollToSection(event: ReactMouseEvent<HTMLAnchorElement>, sectionId: string) {
  event.preventDefault();
  const target = document.getElementById(sectionId);
  if (!target) return;

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.setTimeout(() => {
    target.classList.add('ring-2', 'ring-primary/20', 'ring-offset-4', 'ring-offset-[#f7f8f6]');
    window.setTimeout(() => {
      target.classList.remove('ring-2', 'ring-primary/20', 'ring-offset-4', 'ring-offset-[#f7f8f6]');
    }, 700);
  }, 280);

  const hash = `#${sectionId}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(null, '', hash);
  }
}

export function ExamSelectionView({ model }: { model: ExamSelectionPageModel }) {
  const loginRedirect = buildPreferredWorkspaceHref(model.viewer.activeExamSpace.examCode);
  const headerAccountAction = model.viewer.isAuthenticated
    ? { label: '个人中心', href: '/profile' }
    : { label: '登录', href: `/login?redirect=${encodeURIComponent(loginRedirect)}` };
  const showRedeemHint =
    model.viewer.isAuthenticated && model.viewer.activeExamSpace.entitlementStatus !== 'active';

  return (
    <main className="min-h-screen text-ink-primary" style={{ background: STAGE_BG }}>
      <div className="mx-auto w-full max-w-7xl">
        <header className="relative z-20 flex h-16 items-center justify-between px-6 sm:px-10 lg:px-12">
          <Link to="/" className="inline-flex items-center" aria-label="ExamFlow 首页">
            <BrandLogo variant="full" size={32} />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to={model.hero.primaryAction.href ?? '/redeem'}
              className={cn(
                buttonVariants({ variant: 'primary' }),
                'hidden h-9 rounded-full px-5 text-sm sm:inline-flex'
              )}
            >
              {model.hero.primaryAction.label}
            </Link>
            <Link
              to={headerAccountAction.href}
              className="text-sm font-semibold text-ink-secondary transition-colors hover:text-ink-primary"
            >
              {headerAccountAction.label}
            </Link>
            <a
              href={XIAOHONGSHU_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="小红书"
              title="小红书"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#FF2442] transition-opacity hover:opacity-80"
            >
              <XiaohongshuIcon className="h-7 w-7" />
            </a>
          </div>
        </header>

        <section
          data-testid="exam-selection-hero-visual"
          className="relative grid items-center gap-10 px-6 pb-6 pt-8 sm:px-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-8 lg:px-12 lg:pb-8 lg:pt-10"
        >
          <div className="relative z-10 max-w-xl">
            <p className="mb-5 text-[12px] font-semibold tracking-[0.04em] text-ink-muted">
              {model.hero.eyebrow}
            </p>
            <h1 className="text-[40px] font-black leading-[1.12] tracking-tight sm:text-[52px] lg:text-[56px]">
              <span className="block text-ink-primary">{model.hero.title}</span>
              <span className="mt-1 block text-accent">{model.hero.titleAccent}</span>
            </h1>
            <p className="mt-6 max-w-[440px] text-[15px] leading-7 text-ink-secondary sm:text-base sm:leading-8">
              {model.hero.description}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to={model.hero.primaryAction.href ?? '/redeem'}
                className={cn(
                  buttonVariants({ variant: 'primary' }),
                  'h-12 rounded-full px-8 text-[15px] shadow-[0_10px_28px_rgba(45,59,45,0.22)] transition-transform duration-200 hover:-translate-y-0.5'
                )}
              >
                {model.hero.primaryAction.label}
              </Link>
              <a
                href="#exam-spaces"
                onClick={(event) => scrollToSection(event, 'exam-spaces')}
                className="text-sm font-semibold text-ink-secondary underline-offset-4 transition-colors hover:text-ink-primary hover:underline"
              >
                {model.hero.secondaryAction.label}
              </a>
            </div>
            {showRedeemHint ? (
              <p className="mt-4 text-sm text-ink-secondary">开通后进入对应考试空间。</p>
            ) : null}
          </div>

          <HeroProductProof proof={model.hero.proof} />
        </section>

        <ExamTargetRail exams={model.exams} />

        <section
          id="training-path"
          className="scroll-mt-16 px-6 py-12 sm:px-10 lg:px-12 lg:py-14"
        >
          <ol className="mx-auto grid max-w-4xl grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-0">
            {model.principles.map((item, index) => (
              <li
                key={item.title}
                className={cn(
                  'relative sm:pr-8',
                  index < model.principles.length - 1 &&
                    'sm:after:absolute sm:after:right-3 sm:after:top-[0.85rem] sm:after:h-px sm:after:w-5 sm:after:bg-ink-primary/15 sm:after:content-[""]'
                )}
              >
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold tabular-nums text-primary-light">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base font-black">{item.title}</span>
                </div>
                <p className="mt-2 max-w-[200px] pl-8 text-sm leading-6 text-ink-secondary">
                  {item.body}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <footer className="px-6 pb-10 pt-2 sm:px-10 lg:px-12">
          <BeianFooter className="px-0 pb-0" />
        </footer>
      </div>
    </main>
  );
}

const EXAM_ACCENTS: Record<string, string> = {
  tcf: '#d97745',
  ielts: '#1f4f46',
  toefl: '#2f6b5c',
  jlpt: '#8b4d3b',
  topik: '#3d5a80',
  dele: '#b4533a',
  siele: '#3d6b5c',
};

function ExamTargetRail({ exams }: { exams: ExamSelectionPageModel['exams'] }) {
  return (
    <div
      id="exam-spaces"
      className="relative z-10 scroll-mt-16 px-6 pb-10 pt-4 sm:px-10 lg:px-12 lg:pb-12"
    >
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <p className="text-[11px] font-bold tracking-[0.18em] text-ink-primary/45">考试目标</p>
        <p className="text-[11px] text-ink-secondary">一次只盯一个 · 未开通先兑换</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7 xl:gap-3">
        {exams.map((exam) => (
          <ExamTargetCard key={exam.id} exam={exam} />
        ))}
      </div>
    </div>
  );
}

function ExamTargetCard({ exam }: { exam: ExamSelectionPageModel['exams'][number] }) {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0, active: false });
  const accent = EXAM_ACCENTS[exam.id] ?? '#1f4f46';
  const selected = exam.status === 'selected';
  const locked = exam.status === 'locked';
  const owned = exam.status === 'owned';
  const href = exam.primaryAction.href ?? '/';

  const handleMove = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x, y, active: true });
  };

  return (
    <Link
      ref={cardRef}
      to={href}
      onMouseMove={handleMove}
      onMouseLeave={() => setTilt({ x: 0, y: 0, active: false })}
      className={cn(
        'group relative min-h-[124px] overflow-hidden rounded-xl border bg-white p-4 transition-shadow duration-200',
        selected
          ? 'border-primary/25 shadow-md'
          : owned
            ? 'border-transparent shadow-sm ring-1 ring-ink-primary/8 hover:shadow-md'
            : locked
              ? 'border-ink-primary/8 opacity-80 hover:opacity-100'
              : 'border-ink-primary/8 shadow-sm hover:border-primary/20 hover:shadow-md'
      )}
      style={{
        ...(owned ? { boxShadow: `inset 3px 0 0 ${accent}, 0 1px 2px rgba(28,42,34,0.06)` } : null),
        transform: tilt.active
          ? `perspective(700px) rotateX(${-tilt.y * 5}deg) rotateY(${tilt.x * 6}deg) translateY(-2px)`
          : undefined,
        transition: tilt.active
          ? 'transform 80ms linear, box-shadow 200ms ease'
          : 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 200ms ease',
      }}
    >
      <div className="relative flex items-start justify-between gap-2">
        <span
          className={cn(
            'text-lg font-black tracking-tight',
            locked ? 'text-ink-primary/65' : 'text-ink-primary'
          )}
        >
          {exam.name}
        </span>
        <span
          className={cn(
            'shrink-0 text-[10px] font-bold',
            selected && 'text-primary-light',
            locked && 'text-ink-muted',
            !selected && !locked && !owned && 'text-ink-secondary/80',
            owned && 'rounded-md px-1.5 py-0.5 tracking-wide text-ink-inverse'
          )}
          style={owned ? { backgroundColor: accent } : undefined}
        >
          {exam.statusLabel}
        </span>
      </div>
      <p className="relative mt-2 text-[11px] leading-4 text-ink-secondary">{exam.subtitle}</p>
      <div
        className="relative mt-5 text-[11px] font-semibold tracking-wide transition-transform duration-200 group-hover:translate-x-0.5"
        style={{ color: locked ? undefined : accent }}
      >
        <span className={locked ? 'text-ink-secondary' : undefined}>{exam.primaryAction.label}</span>
      </div>
    </Link>
  );
}

/** BonTCF-style right proof: main session panel + floating metric chip. */
function HeroProductProof({ proof }: { proof: ExamSelectionHeroProof }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [active, setActive] = useState(false);

  const handleMove = (event: ReactMouseEvent<HTMLDivElement>) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setOffset({
      x: (event.clientX - rect.left) / rect.width - 0.5,
      y: (event.clientY - rect.top) / rect.height - 0.5,
    });
  };

  const layer = (depth: number, rotate = 0) => ({
    transform: `translate3d(${offset.x * depth * 18}px, ${offset.y * depth * 12}px, 0) rotate(${
      rotate + offset.x * depth * 2
    }deg)`,
    transition: active ? 'transform 90ms linear' : 'transform 500ms cubic-bezier(0.22, 1, 0.36, 1)',
  });

  return (
    <div
      ref={stageRef}
      className="relative mx-auto w-full max-w-[440px] lg:mx-0 lg:max-w-none"
      onMouseEnter={() => setActive(true)}
      onMouseMove={handleMove}
      onMouseLeave={() => {
        setActive(false);
        setOffset({ x: 0, y: 0 });
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-6 -top-8 h-48 w-48 rounded-full bg-primary/10 blur-3xl"
        style={layer(0.8)}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-4 left-4 h-36 w-36 rounded-full bg-accent/10 blur-3xl"
        style={layer(-0.6)}
      />

      <div
        className="absolute -left-2 top-6 z-20 sm:-left-6 sm:top-10"
        style={layer(1.2, -2)}
      >
        <div className="flex items-center gap-3 rounded-2xl border border-white/80 bg-white px-4 py-3 shadow-[0_18px_40px_rgba(28,42,34,0.12)]">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/12">
            <span className="h-2.5 w-2.5 rounded-sm bg-accent" />
          </span>
          <div>
            <p className="text-xl font-black leading-none tabular-nums text-ink-primary">
              {proof.floatMetricValue}
            </p>
            <p className="mt-1 text-[11px] font-semibold text-ink-muted">{proof.floatMetricLabel}</p>
          </div>
        </div>
      </div>

      <div
        data-testid="exam-selection-hero-proof"
        className="relative z-10 overflow-hidden rounded-2xl border border-ink-primary/6 bg-white shadow-[0_28px_60px_rgba(28,42,34,0.12)]"
        style={layer(0.35, 1)}
      >
        <div className="h-1.5 w-full bg-accent" />
        <div className="p-6 sm:p-7">
          <p className="text-[11px] font-bold tracking-[0.14em] text-accent">
            {proof.nextStepLabel}
          </p>
          <h2 className="mt-3 text-xl font-black tracking-tight text-ink-primary sm:text-2xl">
            {proof.sessionTitle}
          </h2>
          <p className="mt-2 text-sm text-ink-secondary">{proof.sessionSubtitle}</p>

          <div className="mt-6 space-y-2.5">
            <div className="h-2.5 w-[78%] rounded-full bg-ink-primary/10" />
            <div className="h-2.5 w-[58%] rounded-full bg-ink-primary/6" />
            <div className="h-2.5 w-[66%] rounded-full bg-ink-primary/4" />
          </div>

          <div className="mt-6 flex gap-2">
            {['6', '12', '18'].map((n) => (
              <span
                key={n}
                className={cn(
                  'flex h-9 flex-1 items-center justify-center rounded-lg text-sm font-bold tabular-nums',
                  n === proof.floatMetricValue
                    ? 'bg-primary text-ink-inverse'
                    : 'bg-primary/6 text-ink-secondary'
                )}
              >
                {n}
              </span>
            ))}
          </div>

          <div className="mt-7 flex items-end justify-between border-t border-ink-primary/6 pt-5">
            <div>
              <p className="text-[11px] font-semibold text-ink-muted">{proof.metricLabel}</p>
              <p className="mt-1 text-2xl font-black tracking-tight text-accent">
                {proof.metricValue}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-semibold text-ink-muted">{proof.intensityLabel}</p>
              <p className="mt-1 text-sm font-bold tabular-nums text-ink-primary">
                {proof.intensityValue}
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-1.5 text-[11px] font-semibold text-ink-secondary">
            {proof.path.map((step, index) => (
              <span key={step} className="flex items-center gap-1.5">
                {index > 0 ? <span className="text-ink-primary/20">→</span> : null}
                <span className={index === proof.path.length - 1 ? 'text-ink-primary' : undefined}>
                  {step}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExamSelectionPage() {
  const viewer = useLearningViewer();
  const model = buildExamSelectionPageModel(viewer);

  return <ExamSelectionView model={model} />;
}
